import React from "react";
import { Lock, Unlock } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import useParking from "../../../api/parking";
import { useEffect, useState } from "react";

const ParkingGrid = ({
  spots = [],
  currentFloor,
  onSpotClick,
  userId,
  userReservations = {},
  showOnlyUserRelated = false,
}) => {
  const parkingApi = useParking();
  const [spotsAvailability, setSpotsAvailability] = useState({});

  useEffect(() => {
    const checkSpotsAvailability = async () => {
      const availability = {};
      console.log(userReservations);
      for (const spot of spots) {
        if (spot.status === "reserved") {
          const available = await parkingApi.getAvailableDates(spot.id);
          const reserved = await parkingApi.getReservedDates(spot.id);
          console.log("Available dates:", available);
          console.log("Reserved dates:", reserved);

          // Check if all dates are reserved
          const allDatesReserved = available.every((availablePeriod) => {
            const availableStart = new Date(availablePeriod.start_date);
            const availableEnd = new Date(availablePeriod.end_date);

            return reserved.some((reservedPeriod) => {
              const reservedStart = new Date(reservedPeriod.start_time);
              const reservedEnd = new Date(reservedPeriod.end_time);

              return (
                reservedStart <= availableEnd && reservedEnd >= availableStart
              );
            });
          });

          availability[spot.id] = allDatesReserved;
        }
      }
      setSpotsAvailability(availability);
    };

    checkSpotsAvailability();
  }, [spots]);

  // Add safety check for empty spots array
  if (!spots.length) {
    return <div>No parking spots available</div>;
  }

  // Filter spots based on showOnlyUserRelated flag
  const filteredSpots = showOnlyUserRelated
    ? spots.filter(
        (spot) =>
          spot.owner_id === userId || userReservations[spot.id]?.length > 0
      )
    : spots;

  // Group filtered spots by column with safe array access
  const spotsByColumn = filteredSpots
    .filter((spot) => spot.floor === currentFloor)
    .reduce((acc, spot) => {
      const col = spot.column_number || 0;
      const row = spot.row_number || 0;

      if (!acc[col]) {
        acc[col] = [];
      }
      acc[col][row] = spot;
      return acc;
    }, {});

  // Find max columns and rows with safety checks
  const floorSpots = spots.filter((spot) => spot.floor === currentFloor);
  const maxColumn = floorSpots.length
    ? Math.max(...floorSpots.map((s) => s.column_number || 0))
    : 0;
  const maxRow = floorSpots.length
    ? Math.max(...floorSpots.map((s) => s.row_number || 0))
    : 0;

  if (maxColumn === 0 || maxRow === 0) {
    return <div>Invalid parking configuration</div>;
  }

  const getSpotIcon = (spot) => {
    if (spot.owner_id === userId) {
      if (!spot.status)
        return <Lock className="h-4 w-4 absolute top-2 right-2" />;
      if (spot.status === "available")
        return <Unlock className="h-4 w-4 absolute top-2 right-2" />;
    }
    return null;
  };

  const getSpotStyle = (spot) => {
    if (spot.type === "ROAD") return "h-16 w-24";

    const baseStyle =
      "h-16 w-24 border-2 rounded-lg flex items-center justify-center font-bold text-sm transition-all duration-200 relative";

    // Check if user has future reservations for this spot
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
    
    const hasFutureReservations = userReservations[spot.id]?.some(reservation => {
      const reservationEndDate = new Date(reservation.end_time);
      reservationEndDate.setHours(23, 59, 59, 999); // Set to end of day
      return reservationEndDate >= currentDate;
    });

    if (hasFutureReservations) {
      return `${baseStyle} bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800`;
    }

    if (spot.owner_id === userId) {
      return `${baseStyle} bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800`;
    }

    return `${baseStyle} bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 border-neutral-200 dark:border-neutral-700`;
  };

  const canOpenModal = (spot) => {
    // Allow if user owns the spot
    if (spot.owner_id === userId) return true;

    // Allow if user has any reservations for this spot (active, future, or recent)
    // This allows users to view their reservation details even if recently ended
    if (userReservations[spot.id]?.length > 0) return true;

    return false;
  };

  return (
    <div className="flex gap-4 p-6 overflow-auto">
      {Array.from({ length: maxColumn }, (_, colIndex) => (
        <div key={colIndex} className="flex flex-col gap-2">
          {Array.from({ length: maxRow }, (_, rowIndex) => {
            const spot = spotsByColumn[colIndex + 1]?.[rowIndex + 1];

            if (!spot) {
              // Show empty space for grid consistency
              return <div key={rowIndex} className="h-16 w-24" />;
            }

            if (spot.type === "ROAD") {
              return <div key={rowIndex} className="h-16 w-24" />;
            }

            return (
              <TooltipProvider key={rowIndex}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() =>
                        canOpenModal(spot) ? onSpotClick(spot) : null
                      }
                      className={`${getSpotStyle(spot)} ${
                        !canOpenModal(spot)
                          ? "cursor-default"
                          : "cursor-pointer hover:scale-105 transition-transform"
                      }`}
                    >
                      {spot.spot_number}
                      {getSpotIcon(spot)}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Spot: {spot.spot_number}</p>
                    {spot.owner_id === userId && <p>Your assigned spot</p>}
                    {userReservations[spot.id]?.length > 0 && (
                      <div className="mt-2">
                        <p>Your reservations:</p>
                        {userReservations[spot.id].map((res) => {
                          const currentDate = new Date();
                          currentDate.setHours(0, 0, 0, 0);
                          const reservationEndDate = new Date(res.end_time);
                          reservationEndDate.setHours(23, 59, 59, 999);
                          const isActive = reservationEndDate >= currentDate;
                          
                          return (
                            <p key={res.id} className={`text-sm ${isActive ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                              {new Date(res.start_time).toLocaleDateString()} -
                              {new Date(res.end_time).toLocaleDateString()}
                              {isActive ? ' (Active)' : ' (Ended)'}
                            </p>
                          );
                        })}
                      </div>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default ParkingGrid;
